import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";

type CB = { onSuccess?: (r: any) => void; onError?: (e: any) => void };

// ── Job Types ──────────────────────────────────────────────────────────────

export const useJobTypeList = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOB_TYPES(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOB_TYPES(cid), { params }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useJobTypeCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_JOB_TYPE_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOB_TYPES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useJobTypeUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_JOB_TYPE_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOB_TYPES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useJobTypeDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CJ_JOB_TYPE_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOB_TYPES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Pipelines + Stages ─────────────────────────────────────────────────────

export const usePipelineList = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_PIPELINES(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_PIPELINES(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const usePipelineCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_PIPELINE_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_PIPELINES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useStageCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_STAGE_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_PIPELINES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Checklists ─────────────────────────────────────────────────────────────

export const useChecklistList = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_CHECKLISTS(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CJ_CHECKLISTS(cid), { params }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useChecklistCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_CHECKLIST_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_CHECKLISTS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useChecklistUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_CHECKLIST_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_CHECKLISTS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useChecklistDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CJ_CHECKLIST_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_CHECKLISTS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Checklist Detail ───────────────────────────────────────────────────────

export const useChecklistDetail = (cid: number, id: number | null) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_CHECKLIST_DETAIL(cid, id!), id],
    queryFn: () => client.get(API_ENDPOINTS.CJ_CHECKLIST_DETAIL(cid, id!)),
    select: (r: any) => r.data.data as any,
    enabled: !!cid && !!id,
    refetchOnWindowFocus: false,
  });

// ── Form Detail ────────────────────────────────────────────────────────────

export const useFormDetail = (cid: number, id: number | null) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_FORM_DETAIL(cid, id!), id],
    queryFn: () => client.get(API_ENDPOINTS.CJ_FORM_DETAIL(cid, id!)),
    select: (r: any) => r.data.data as any,
    enabled: !!cid && !!id,
    refetchOnWindowFocus: false,
  });

// ── Form Templates ─────────────────────────────────────────────────────────

export const useFormList = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_FORMS(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CJ_FORMS(cid), { params }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useFormCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_FORM_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_FORMS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useFormUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_FORM_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_FORMS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useFormDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CJ_FORM_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_FORMS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};
